import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, MonitorPlay } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { StatusChip, type Tone } from "@/components/status";
import { WS_BASE } from "@/lib/api";
import { useCancelLogin, useStartLogin } from "@/lib/queries";
import { LiveLoginMessage, type LiveLoginState } from "@/lib/schemas";
import type { IdentityHealth } from "@/lib/schemas";

/**
 * A Facebook login, running on the server, drawn here.
 *
 * What is on this canvas is a real login form on a real account, and what
 * the operator types goes into it. Two things follow, and both are
 * deliberate rather than incidental:
 *
 *   NOTHING IS KEPT. Frames are drawn to a canvas and replaced. They are not
 *   collected into an array, not put in state that React would retain, not
 *   written to storage and not sent anywhere. The only copy of a frame is
 *   the pixels currently on screen.
 *
 *   NOTHING COMES BACK. The socket carries frames and a state word in one
 *   direction, and mouse and key events in the other. The session cookies
 *   this login produces never leave the server -- they are sealed into the
 *   identity's row, and this view is told "logged in".
 *
 * The browser runs on the machine that will USE the session, not on the
 * operator's laptop, and that is the reason this exists at all: a login from
 * a home IP followed by that session being used from a datacenter is what
 * trips Facebook's checkpoints.
 */

/** The server's frame size. The canvas is fixed to it so coordinates match. */
const FRAME_WIDTH = 1280;
const FRAME_HEIGHT = 800;

const STATE_TONE: Record<LiveLoginState, Tone> = {
  starting: "muted",
  waiting: "warn",
  logged_in: "ok",
  timed_out: "danger",
  cancelled: "muted",
  failed: "danger",
};

const STATE_LABEL: Record<LiveLoginState, string> = {
  starting: "starting",
  waiting: "waiting",
  logged_in: "logged in",
  timed_out: "timed out",
  cancelled: "cancelled",
  failed: "failed",
};

const STATE_HELP: Record<LiveLoginState, string> = {
  starting: "Opening a browser. A few seconds.",
  waiting: "Type the email, password and any code into the view. It finishes by itself.",
  logged_in: "Saved against this identity. The browser is closed.",
  timed_out: "Timed out after ten minutes. Nothing changed.",
  cancelled: "Cancelled. Nothing changed.",
  failed: "The browser stopped. Nothing changed.",
};

/** CDP's modifier bitfield: Alt=1, Ctrl=2, Meta=4, Shift=8. */
function modifiersOf(event: { altKey: boolean; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }): number {
  return (
    (event.altKey ? 1 : 0) | (event.ctrlKey ? 2 : 0) | (event.metaKey ? 4 : 0) | (event.shiftKey ? 8 : 0)
  );
}

export function LiveLoginDialog({
  identity,
  onClose,
}: {
  identity: IdentityHealth | null;
  onClose: () => void;
}) {
  const start = useStartLogin();
  const cancel = useCancelLogin();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const [state, setState] = useState<LiveLoginState>("starting");
  const [error, setError] = useState<string | null>(null);

  const identityId = identity?.id ?? null;

  const send = useCallback((payload: unknown) => {
    const socket = socketRef.current;
    if (socket !== null && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(payload));
    }
  }, []);

  useEffect(() => {
    if (identityId === null) return;
    let disposed = false;
    setState("starting");
    setError(null);

    void start
      .mutateAsync(identityId)
      .then(({ login }) => {
        if (disposed) return;
        setState(login.state);
        const socket = new WebSocket(
          `${WS_BASE}/ops/live-login?ticket=${encodeURIComponent(login.ticket)}`,
        );
        socketRef.current = socket;

        socket.onmessage = (event: MessageEvent<string>) => {
          const parsed = LiveLoginMessage.safeParse(
            JSON.parse(typeof event.data === "string" ? event.data : "null") as unknown,
          );
          if (!parsed.success) return;
          if (parsed.data.type === "state") {
            setState(parsed.data.state);
            return;
          }
          // Drawn and dropped. The frame is never retained: this is a
          // picture of somebody's password being typed.
          const canvas = canvasRef.current;
          if (canvas === null) return;
          const context = canvas.getContext("2d");
          if (context === null) return;
          const image = new Image();
          image.onload = () => {
            context.drawImage(image, 0, 0, canvas.width, canvas.height);
          };
          image.src = `data:image/jpeg;base64,${parsed.data.data}`;
        };

        socket.onerror = () => {
          if (!disposed) setError("The live view's connection failed.");
        };
      })
      .catch((cause: Error) => {
        if (!disposed) {
          setState("failed");
          setError(cause.message);
        }
      });

    return () => {
      disposed = true;
      socketRef.current?.close();
      socketRef.current = null;
    };
    // `start` is a stable mutation object; re-running this on its identity
    // would open a second socket for one login.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityId]);

  /** Canvas pixel coordinates, whatever size the element was laid out at. */
  const pointAt = (event: React.MouseEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * FRAME_WIDTH,
      y: ((event.clientY - rect.top) / rect.height) * FRAME_HEIGHT,
    };
  };

  const live = state === "starting" || state === "waiting";

  function finish(): void {
    if (identityId !== null && live) cancel.mutate(identityId);
    onClose();
  }

  return (
    <Dialog open={identity !== null} onOpenChange={(open) => (open ? undefined : finish())}>
      <DialogContent className="w-[min(84rem,calc(100vw-2rem))]">
        <DialogTitle>Log in as {identity?.label ?? ""}</DialogTitle>
        <DialogDescription>
          This browser runs on the collector, not on your machine.
        </DialogDescription>

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <StatusChip tone={STATE_TONE[state]} icon={state === "starting" ? "pause" : "default"}>
            {STATE_LABEL[state]}
          </StatusChip>
          <p className="min-w-0 flex-1 text-xs text-muted-foreground">{STATE_HELP[state]}</p>
          <Button variant="outline" size="sm" onClick={finish}>
            {live ? "Cancel" : "Close"}
          </Button>
        </div>

        {/* Announced, because a screen reader gets no canvas and no spinner:
            ten minutes of silence is indistinguishable from a dead view. */}
        <p role="status" aria-live="polite" className="sr-only">
          {STATE_LABEL[state]}. {STATE_HELP[state]}
        </p>

        {error !== null ? (
          <p className="mt-3 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-3 overflow-hidden rounded-md border border-border bg-black">
          <canvas
            ref={canvasRef}
            width={FRAME_WIDTH}
            height={FRAME_HEIGHT}
            tabIndex={0}
            role="application"
            aria-label={`Live browser for ${identity?.label ?? ""}`}
            data-testid="live-login-canvas"
            className="block w-full cursor-default outline-none"
            onMouseDown={(event) => {
              event.currentTarget.focus();
              send({ kind: "mouse", type: "mousePressed", ...pointAt(event), button: "left", clickCount: event.detail, modifiers: modifiersOf(event) });
            }}
            onMouseUp={(event) =>
              send({ kind: "mouse", type: "mouseReleased", ...pointAt(event), button: "left", clickCount: event.detail, modifiers: modifiersOf(event) })
            }
            onMouseMove={(event) =>
              send({ kind: "mouse", type: "mouseMoved", ...pointAt(event), modifiers: modifiersOf(event) })
            }
            onWheel={(event) =>
              send({
                kind: "mouse",
                type: "mouseWheel",
                x: FRAME_WIDTH / 2,
                y: FRAME_HEIGHT / 2,
                deltaX: -event.deltaX,
                deltaY: -event.deltaY,
              })
            }
            onKeyDown={(event) => {
              // The browser's default would scroll this page or move focus
              // out of the canvas mid-password.
              event.preventDefault();
              send({
                kind: "key",
                type: "keyDown",
                key: event.key,
                code: event.code,
                windowsVirtualKeyCode: event.keyCode,
                modifiers: modifiersOf(event),
              });
              // A printable key needs a `char` event as well, or Chromium
              // registers the keypress and inserts nothing.
              if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
                send({ kind: "key", type: "char", text: event.key, modifiers: modifiersOf(event) });
              }
            }}
            onKeyUp={(event) => {
              event.preventDefault();
              send({
                kind: "key",
                type: "keyUp",
                key: event.key,
                code: event.code,
                windowsVirtualKeyCode: event.keyCode,
                modifiers: modifiersOf(event),
              });
            }}
          />
        </div>

        <p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          {live ? (
            <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <MonitorPlay aria-hidden="true" className="h-3.5 w-3.5" />
          )}
          Click the view first, then type. It closes after ten minutes.
        </p>
      </DialogContent>
    </Dialog>
  );
}
